import React from 'react';
import { Text } from 'pounceV2';
import { useTheme } from '@mui/material';

interface HighlightRange {
  beginOffset: number;
  endOffset: number;
}

interface HighlightedTextProps {
  text: string;
  highlights?: HighlightRange[] | null;
}

const HighlightedText = ({ text, highlights }: HighlightedTextProps) => {
  const {
    palette: { componentTokens },
  } = useTheme();
  if (!highlights || highlights.length === 0) {
    return <>{text}</>;
  }

  const sortedHighlights = [...highlights].sort((a, b) => a.beginOffset - b.beginOffset);
  const parts: React.ReactNode[] = [];
  let lastOffset = 0;

  sortedHighlights.forEach((highlight, index) => {
    // Guard against overlapping ranges
    const start = Math.max(highlight.beginOffset, lastOffset);
    if (start > lastOffset) {
      parts.push(
        <React.Fragment key={`text-${index}`}>{text.slice(lastOffset, start)}</React.Fragment>,
      );
    }
    if (start < highlight.endOffset) {
      parts.push(
        <Text
          key={`highlight-${index}`}
          as="span"
          backgroundColor={componentTokens.tags.colors.info.background.resting}
          color={componentTokens.tags.colors.info.text.labelBase}
          fontWeight="bold"
          px={0.5}
          borderRadius="small"
        >
          {text.slice(start, highlight.endOffset)}
        </Text>,
      );
    }
    lastOffset = Math.max(lastOffset, highlight.endOffset);
  });

  if (lastOffset < text.length) {
    parts.push(<React.Fragment key="text-end">{text.slice(lastOffset)}</React.Fragment>);
  }

  return <>{parts}</>;
};

export default HighlightedText;
